import { useCallback, useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { StatusPill, SITE_LABEL } from "@/components/Layout";
import { toast } from "sonner";
import { Check, X, Download, RefreshCw, Pencil } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Pagination, usePagination } from "@/components/Pagination";

const LEAVE_LABELS = {
  ANNUAL: "Annual", SICK: "Sick", BEREAVEMENT: "Bereavement",
  PARENTAL: "Parental", ALTERNATIVE_HOLIDAYS: "Alt Holidays",
  PUBLIC_HOLIDAY: "Public", UNPAID: "Unpaid", TOIL: "TOIL",
};

// Xero-sync badge mirrors backend xero_sync_status on leave_requests.
const XERO_SYNC_LABEL = {
  SYNCED: { text: "Synced · Xero", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  FAILED: { text: "Xero: failed", cls: "bg-red-50 text-red-700 border-red-200" },
  SKIPPED_NO_CALENDAR: { text: "Xero: no calendar", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  SKIPPED_NO_XERO_EMPLOYEE: { text: "Xero: not linked", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  SKIPPED_NO_CONNECTION: { text: "Xero: offline", cls: "bg-gray-100 text-gray-600 border-gray-200" },
  SKIPPED_NO_TYPE_MATCH: { text: "Xero: type", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  AUTO_SYNC_DISABLED: { text: "Xero: auto-sync off", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  NONE: null,
};

function _relTime(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diff = Date.now() - t;
  if (diff < 0) return "in the future";
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

function XeroSyncBadge({ leave, onRetry, busyId }) {
  if (leave.status !== "APPROVED") return null;
  const meta = XERO_SYNC_LABEL[leave.xero_sync_status];
  if (!meta) return null;
  const synced_at = leave.xero_synced_at;
  const tooltip = leave.xero_last_error || (synced_at ? `Last sync ${new Date(synced_at).toLocaleString()}` : "");
  const isAutoOff = leave.xero_sync_status === "AUTO_SYNC_DISABLED";
  const isBusy = busyId === leave.id;
  return (
    <span className="inline-flex flex-col gap-0.5" data-testid={`xero-status-${leave.id}`}>
      <span className="inline-flex items-center gap-1 flex-wrap">
        <Badge variant="outline" className={`text-[10px] ${meta.cls}`} title={tooltip}>
          {meta.text}
        </Badge>
        {isAutoOff ? (
          <button
            type="button"
            onClick={() => onRetry(leave.id)}
            disabled={isBusy}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full border border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100 disabled:opacity-50"
            title="Push this leave to Xero now (bypasses auto-sync toggle)"
            data-testid={`sync-now-${leave.id}`}
          >
            <RefreshCw className={`h-3 w-3 ${isBusy ? "animate-spin" : ""}`} />
            Sync now
          </button>
        ) : leave.xero_sync_status !== "SYNCED" && (
          <button
            type="button"
            onClick={() => onRetry(leave.id)}
            disabled={isBusy}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
            title={leave.xero_last_error || "Retry Xero sync"}
            data-testid={`retry-xero-${leave.id}`}
          >
            <RefreshCw className={`h-3 w-3 text-gray-500 ${isBusy ? "animate-spin" : ""}`} />
          </button>
        )}
      </span>
      {leave.xero_sync_status === "SYNCED" && synced_at && (
        <span className="text-[10px] text-gray-500" data-testid={`xero-synced-at-${leave.id}`}>
          synced {_relTime(synced_at)}
        </span>
      )}
      {isAutoOff && leave.xero_auto_sync_disabled_at && (
        <span className="text-[10px] text-sky-700/70" data-testid={`xero-deferred-at-${leave.id}`}>
          deferred {_relTime(leave.xero_auto_sync_disabled_at)}
        </span>
      )}
    </span>
  );
}

function HoursEditor({ periods, setPeriods, idPrefix }) {
  const total = periods.reduce((s, p) => s + (Number(p.hours) || 0), 0);
  return (
    <div className="border border-gray-200 rounded-md" data-testid={`${idPrefix}-hours-editor`}>
      <div className="flex justify-between items-center px-3 py-2 border-b border-gray-100">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">Pay period</span>
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">Hours</span>
      </div>
      {periods.length === 0 && (
        <p className="px-3 py-3 text-xs text-gray-500">No weekly breakdown was submitted with this request.</p>
      )}
      {periods.map((p, i) => (
        <div key={p.week_ending} className="flex justify-between items-center px-3 py-2 border-b last:border-b-0 border-gray-100 gap-3">
          <span className="text-sm text-[#1A2744]">Week ending {p.week_ending}</span>
          <Input
            type="number"
            min="0"
            step="0.25"
            className="w-24 h-9 text-right"
            value={p.hours}
            onChange={(e) => {
              const next = [...periods];
              next[i] = { ...next[i], hours: e.target.value };
              setPeriods(next);
            }}
            data-testid={`${idPrefix}-hours-${p.week_ending}`}
          />
        </div>
      ))}
      <div className="flex justify-between items-center px-3 py-2 border-t-2 border-gray-200 bg-gray-50">
        <span className="text-sm font-semibold text-[#1A2744]">Total</span>
        <span className="text-sm font-bold text-[#1A2744]" data-testid={`${idPrefix}-total`}>{total.toFixed(2)}h</span>
      </div>
    </div>
  );
}

function DecisionDialog({ leave, type, onDone }) {
  const id = leave.id;
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [periods, setPeriods] = useState([]);
  useEffect(() => {
    if (open) setPeriods((leave.periods || []).map((p) => ({ ...p })));
  }, [open, leave.periods]);
  const submit = async () => {
    try {
      const payload = { note };
      if (type === "approve" && periods.length > 0) {
        payload.periods = periods.map((p) => ({ week_ending: p.week_ending, hours: Number(p.hours) || 0 }));
      }
      await api.post(`/admin/leave/${id}/${type}`, payload);
      toast.success(type === "approve" ? "Approved" : "Declined");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant={type === "approve" ? "default" : "outline"} className={type === "approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "text-red-600"} data-testid={`${type}-${id}`}>
          {type === "approve" ? <Check className="h-3.5 w-3.5 mr-1" /> : <X className="h-3.5 w-3.5 mr-1" />}
          {type === "approve" ? "Approve" : "Decline"}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{type === "approve" ? "Approve leave" : "Decline leave"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {type === "approve" && (
            <>
              <p className="text-xs text-gray-500">
                {leave.user_name} · {leave.start_date} → {leave.end_date}. Adjust the hours below if they don't match what was worked.
              </p>
              <HoursEditor periods={periods} setPeriods={setPeriods} idPrefix={`approve-${id}`} />
            </>
          )}
          <Input placeholder="Optional note to employee" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`decision-note-${id}`} />
        </div>
        <DialogFooter>
          <Button onClick={submit} className="bg-[#1A2744] text-white" data-testid={`decision-confirm-${id}`}>Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditHoursDialog({ leave, onDone }) {
  const [open, setOpen] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setPeriods((leave.periods || []).map((p) => ({ ...p }))); setReason(""); }
  }, [open, leave.periods]);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/leave/${leave.id}/hours`, {
        periods: periods.map((p) => ({ week_ending: p.week_ending, hours: Number(p.hours) || 0 })),
        reason: reason.trim(),
      });
      toast.success(`Hours updated to ${Number(data.leave.total_hours).toFixed(2)}h`);
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the hours");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`edit-hours-${leave.id}`}>
          <Pencil className="h-3.5 w-3.5 mr-1" /> Hours
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit approved hours</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-gray-500">
            {leave.user_name} · {leave.start_date} → {leave.end_date}. The employee's leave balance is adjusted by the difference. Only possible until this request syncs to Xero.
          </p>
          <HoursEditor periods={periods} setPeriods={setPeriods} idPrefix={`edit-${leave.id}`} />
          <Input
            placeholder="Reason (required — written to audit log)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid={`edit-hours-reason-${leave.id}`}
          />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="bg-[#1A2744] text-white" data-testid={`edit-hours-save-${leave.id}`}>
            {busy ? "Saving…" : "Save hours"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminLeave() {
  const [leave, setLeave] = useState([]);
  const [tab, setTab] = useState("PENDING");
  const [busyId, setBusyId] = useState(null);
  const [syncingBalances, setSyncingBalances] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/leave?status_filter=${tab === "ALL" ? "" : tab}`);
    setLeave(data.leave);
  }, [tab]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const pg = usePagination(leave, 10);

  const syncBalancesFromXero = async () => {
    setSyncingBalances(true);
    try {
      const { data } = await api.post("/admin/xero/sync-leave-balances");
      const synced = (data.synced || []).length;
      const failed = (data.failed || []).length;
      const skipped = (data.skipped || []).length;
      if (synced > 0 && failed === 0) {
        toast.success(`Synced ${synced} employee${synced === 1 ? "" : "s"} from Xero.`);
      } else if (synced > 0 && failed > 0) {
        toast.warning(`Synced ${synced}, failed ${failed}. Check audit log for details.`);
      } else if (failed > 0) {
        toast.error(`Bulk balance sync failed for ${failed} employee${failed === 1 ? "" : "s"}.`);
      } else {
        toast.message(skipped ? `Skipped ${skipped} (no Xero link).` : "Nothing to sync.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk balance sync failed");
    } finally {
      setSyncingBalances(false);
    }
  };

  const retryXero = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/admin/leave/${id}/retry-xero`);
      const st = data?.xero?.status;
      if (st === "SYNCED") toast.success("Synced to Xero");
      else if (st === "AUTO_SYNC_DISABLED") toast.error("Unexpected — bulk path should bypass the toggle. Check backend logs.");
      else toast.message(`Xero: ${st || "unknown"}`, {
        description: data?.xero?.error || "See leave row for details.",
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-leave-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744]">Leave approvals</h1>
          <p className="text-gray-500 mt-1">Approve or decline requests. Decisions notify the employee.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={syncBalancesFromXero}
            disabled={syncingBalances}
            data-testid="admin-leave-sync-balances"
            className="gap-1.5"
          >
            <RefreshCw className={`h-4 w-4 ${syncingBalances ? "animate-spin" : ""}`} />
            {syncingBalances ? "Syncing balances…" : "Sync balances from Xero"}
          </Button>
          <a href={`${API_BASE}/admin/leave/export.csv`} target="_blank" rel="noreferrer">
            <Button variant="outline" data-testid="export-leave"><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
          </a>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="PENDING" data-testid="leave-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="APPROVED" data-testid="leave-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="DECLINED" data-testid="leave-tab-declined">Declined</TabsTrigger>
          <TabsTrigger value="ALL" data-testid="leave-tab-all">All</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="md:hidden divide-y divide-gray-100 px-4" data-testid="admin-leave-cards">
                {pg.pageItems.length === 0 && <p className="py-8 text-center text-gray-500">Nothing to show.</p>}
                {pg.pageItems.map((l) => (
                  <div key={l.id} className="py-3 space-y-2" data-testid={`admin-leave-card-${l.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-[#1A2744] truncate">{l.user_name}</p>
                        <p className="text-sm text-gray-600">{l.start_date} → {l.end_date}</p>
                        <p className="text-[11px] text-gray-400">
                          {LEAVE_LABELS[l.leave_type]} · {SITE_LABEL[l.site]} · submitted {new Date(l.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right shrink-0 space-y-1">
                        <StatusPill status={l.status} />
                        <p className="text-sm font-semibold text-[#1A2744]">
                          {l.total_hours != null ? `${Number(l.total_hours).toFixed(1)}h` : (l.days ?? "—")}
                          {l.hours_edited_by && <span className="ml-1 text-[10px] font-normal text-amber-700">edited</span>}
                        </p>
                      </div>
                    </div>
                    {(l.description || l.reason) && (
                      <p className="text-xs text-gray-500 line-clamp-2">{l.description || l.reason}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <XeroSyncBadge leave={l} onRetry={retryXero} busyId={busyId} />
                      {l.status === "PENDING" && (
                        <>
                          <DecisionDialog leave={l} type="approve" onDone={load} />
                          <DecisionDialog leave={l} type="decline" onDone={load} />
                        </>
                      )}
                      {l.status === "APPROVED" && l.xero_sync_status !== "SYNCED" && (
                        <EditHoursDialog leave={l} onDone={load} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Hours</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leave.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-gray-500 py-8">Nothing to show.</TableCell></TableRow>}
                  {pg.pageItems.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-xs text-gray-500">{new Date(l.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="font-medium">{l.user_name}</TableCell>
                      <TableCell className="text-xs text-gray-500">{SITE_LABEL[l.site]}</TableCell>
                      <TableCell>{LEAVE_LABELS[l.leave_type]}</TableCell>
                      <TableCell>{l.start_date} → {l.end_date}</TableCell>
                      <TableCell>
                        {l.total_hours != null ? `${Number(l.total_hours).toFixed(1)}` : (l.days ?? "—")}
                        {l.hours_edited_by && (
                          <span
                            className="ml-1 text-[10px] text-amber-700"
                            title={`Edited by ${l.hours_edited_by}${l.hours_edit_reason ? ` — ${l.hours_edit_reason}` : ""}`}
                            data-testid={`hours-edited-${l.id}`}
                          >
                            edited
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500 max-w-[200px] truncate">{l.description || l.reason || "—"}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <StatusPill status={l.status} />
                          <XeroSyncBadge leave={l} onRetry={retryXero} busyId={busyId} />
                        </div>
                      </TableCell>
                      <TableCell className="flex gap-1">
                        {l.status === "PENDING" && (
                          <>
                            <DecisionDialog leave={l} type="approve" onDone={load} />
                            <DecisionDialog leave={l} type="decline" onDone={load} />
                          </>
                        )}
                        {l.status === "APPROVED" && l.xero_sync_status !== "SYNCED" && (
                          <EditHoursDialog leave={l} onDone={load} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </CardContent>
          </Card>
          <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total} pageSize={pg.pageSize} onPageChange={pg.setPage} idPrefix="admin-leave" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
